import React, { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useAction, useMutation, useQuery } from 'convex/react'
import { api } from '../../convex/_generated/api'
import QueryWrapper from '../components/QueryWrapper'
import ErrorDisplay from '../components/ErrorDisplay'
import AdjustersDropdown from '../components/AdjustersDropdown'
// removed useAuth; using getCurrentUser query instead

// Lightweight file size formatter for UI display
function formatFileSize(size: number | undefined | null): string {
  const s = typeof size === 'number' ? size : 0
  if (s < 1024) return `${s} B`
  const kb = s / 1024
  if (kb < 1024) return `${Math.round(kb)} KB`
  const mb = kb / 1024
  return `${mb.toFixed(1)} MB`
}

const ClaimDetailsPage: React.FC = () => {
  const { claimId } = useParams<{ claimId: string }>()
  const currentUser = useQuery(api.auth.getCurrentUser)
  const [requesterId, setRequesterId] = useState<string | null>(null)
  React.useEffect(() => {
    if (currentUser && currentUser._id) {
      setRequesterId(currentUser._id)
    }
  }, [currentUser])
  const claim = useQuery(api.claims.getClaimByNumber as any, requesterId && claimId ? {
    requesterId,
    claimNumber: claimId,
  } : 'skip') as any
  // Adjusters query moved to AdjustersDropdown component for better error handling

  const updateStatus = useMutation(api.claims.updateClaimStatus as any)
  const assignClaim = useMutation(api.claims.assignClaim as any)
  const updateDetails = useMutation(api.claims.updateClaimDetails as any)
  const requestApproval = useMutation(api.claims.requestApproval as any)
  const approveClaim = useMutation(api.claims.approveClaim as any)
  const getDocumentDownloadUrl = useMutation(api.documents.getDocumentDownloadUrl as any)
  const listDocuments = useQuery as any
  const recentDocAudits = useQuery(api.documents.getRecentDocumentAuditLogs as any, { limit: 5 } as any) as any[] | undefined
  const claimDocs = listDocuments(api.documents.getDocumentsByClaim as any, requesterId && claim?._id ? { claimId: claim._id, requesterId } : 'skip') as any[] | 'skip'

  const [statusModalOpen, setStatusModalOpen] = useState(false)
  // const uploadDocument = useMutation(api.documents.uploadDocument as any)
  const getUploadUrl = useAction(api.documents.getUploadUrl as any)
  const finalizeUpload = useMutation(api.documents.finalizeUpload as any)
  const [activeTab, setActiveTab] = useState<'details' | 'documents'>('details')
  const TabButton: React.FC<{active: boolean; onClick: () => void; testid: string; children: React.ReactNode}> = ({ active, onClick, testid, children }) => (
    <button data-testid={testid} onClick={onClick} style={{
      padding: '8px 12px',
      borderRadius: 8,
      border: active ? '1px solid #53389E' : '1px solid #D0D5DD',
      background: active ? '#F9F5FF' : '#fff',
      color: active ? '#53389E' : '#344054',
      fontWeight: active ? 600 : 500,
      cursor: 'pointer'
    }}>{children}</button>
  )
  const [assignmentModalOpen, setAssignmentModalOpen] = useState(false)
  const [newStatus, setNewStatus] = useState('investigating')
  const [statusNote, setStatusNote] = useState('')
  const [assigneeId, setAssigneeId] = useState('')
  const [assignmentSuccess, setAssignmentSuccess] = useState('')
  const [approvalOpen, setApprovalOpen] = useState(false)
  const [approvedAmount, setApprovedAmount] = useState('')
  const [approvalNotes, setApprovalNotes] = useState('')
  // Document filters (must be top-level hooks)
  const [docSearchTerm, setDocSearchTerm] = useState('')
  const [docTypeFilter, setDocTypeFilter] = useState('')
  // Shared read-only field styles for a professional layout
  const roInputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #D0D5DD',
    borderRadius: 8,
    background: '#F9FAFB',
    color: '#101828',
  };
  const roTextAreaStyle: React.CSSProperties = {
    ...roInputStyle,
    minHeight: 80,
    resize: 'vertical' as const,
    whiteSpace: 'pre-wrap' as const,
  };

  const canSubmitStatus = useMemo(() => !!newStatus && !!requesterId && !!claim?._id, [newStatus, requesterId, claim])
  const canAssign = useMemo(() => !!assigneeId && !!requesterId && !!claim?._id, [assigneeId, requesterId, claim])
  const docs = Array.isArray(claimDocs) ? claimDocs : []
  const filteredDocs = useMemo(() => {
    const term = docSearchTerm.trim().toLowerCase()
    return docs.filter((d: any) => {
      const matchesType = !docTypeFilter || String(d.documentType) === docTypeFilter
      const matchesTerm = !term || String(d.fileName || '').toLowerCase().includes(term) || String(d.description || '').toLowerCase().includes(term)
      return matchesType && matchesTerm
    })
  }, [docs, docSearchTerm, docTypeFilter])

  const submitStatus = async () => {
    if (!canSubmitStatus) return
    await updateStatus({ claimId: claim._id, status: newStatus, updatedBy: requesterId, reason: statusNote })
    setStatusModalOpen(false)
  }

  const submitAssignment = async () => {
    if (!canAssign) return
    await assignClaim({ claimId: claim._id, assignedTo: assigneeId, assignedBy: requesterId })
    setAssignmentSuccess('Assigned successfully')
    setAssignmentModalOpen(false)
  }

  return (
    <div className="claim-details-page">
      <div className="page-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ marginBottom: 4 }}>Claim Details</h1>
            <p data-testid="claim-details" style={{ margin: 0, color: '#667085' }}>Claim #: {claimId}</p>
          </div>
          {claim && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{
                padding: '6px 10px',
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 600,
                background: claim.status === 'approved' ? '#ECFDF3' : claim.status === 'denied' ? '#FEF3F2' : claim.status === 'closed' ? '#F2F4F7' : '#EEF2FF',
                color: claim.status === 'approved' ? '#027A48' : claim.status === 'denied' ? '#B42318' : claim.status === 'closed' ? '#344054' : '#3538CD',
                border: '1px solid rgba(0,0,0,0.06)'
              }}>
                {claim.status.toUpperCase()}
              </span>
              <span style={{ color: '#667085', fontSize: 12 }}>Created {new Date(claim.createdAt).toLocaleDateString()}</span>
            </div>
          )}
        </div>
      </div>

      <div className="claim-details-content">
        {claim ? (
          <div className="claim-details-panel" style={{ background: '#fff', border: '1px solid #EAECF0', borderRadius: 12, boxShadow: '0 1px 2px rgba(16,24,40,0.04)', padding: 16 }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12, borderBottom: '1px solid #EAECF0', paddingBottom: 8 }}>
              <TabButton testid="details-tab" active={activeTab==='details'} onClick={() => setActiveTab('details')}>Details</TabButton>
              <TabButton testid="documents-tab" active={activeTab==='documents'} onClick={() => setActiveTab('documents')}>Documents</TabButton>
            </div>

            {activeTab === 'details' && (
            <>
            <h3 style={{ margin: '0 0 12px', fontSize: 16, color: '#101828' }}>Core Information</h3>
            <div className="details-grid" style={{ gridTemplateColumns: 'repeat(4, minmax(0,1fr))' }}>
              <div>
                <label className="field-label">Claim Number</label>
                <input readOnly style={roInputStyle} value={claim.claimNumber} />
              </div>
              <div>
                <label className="field-label">Policy Number</label>
                <input readOnly style={roInputStyle} value={claim.policyNumber} />
              </div>
              <div>
                <label className="field-label">Type</label>
                <input readOnly style={roInputStyle} value={claim.claimType} />
              </div>
              <div>
                <label className="field-label">Status</label>
                <input readOnly data-testid="claim-status" style={roInputStyle} value={claim.status} />
              </div>
              <div>
                <label className="field-label">Assigned To</label>
                <input readOnly data-testid="assigned-to" style={roInputStyle} value={claim.assignedTo || 'Unassigned'} />
              </div>
            </div>
              <h3 style={{ margin: '20px 0 12px', fontSize: 16, color: '#101828' }}>Policy & Dates</h3>
              <div className="details-grid" style={{ marginTop: 4, gridTemplateColumns: 'repeat(4, minmax(0,1fr))' }}>
                <div>
                  <label className="field-label">Policy Holder</label>
                  <input readOnly style={roInputStyle} value={`${claim.policyHolderFirstName || ''} ${claim.policyHolderLastName || ''}`} />
                </div>
                <div>
                  <label className="field-label">Insured</label>
                  <input readOnly style={roInputStyle} value={`${claim.insuredFirstName || ''} ${claim.insuredLastName || ''}`} />
                </div>
                <div>
                  <label className="field-label">Reported Date</label>
                  <input readOnly style={roInputStyle} value={new Date(claim.reportedDate).toLocaleDateString()} />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label className="field-label">Coverage Summary</label>
                  <textarea readOnly style={roTextAreaStyle} value={claim.coverageSummary || '—'} />
                </div>
                {Array.isArray(claim.riders) && claim.riders.length > 0 && (
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label className="field-label">Riders</label>
                    <textarea readOnly style={roTextAreaStyle} value={(claim.riders as string[]).join('\n')} />
                  </div>
                )}
                {Array.isArray(claim.beneficiaries) && claim.beneficiaries.length > 0 && (
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label className="field-label">Beneficiaries</label>
                    <textarea readOnly style={roTextAreaStyle} value={claim.beneficiaries.map((b: any) => `${b.name} (${b.relationship}${b.percentage != null ? ` ${b.percentage}%` : ''})`).join('\n')} />
                  </div>
                )}
              </div>
            <div className="details-actions" style={{ display: 'flex', gap: 12, marginTop: 16 }}>
              <button data-testid="update-status-button" className="primary" onClick={() => setStatusModalOpen(true)} style={{ padding: '10px 14px', borderRadius: 8 }}>Update Status</button>
              <button data-testid="assign-claim-button" onClick={() => setAssignmentModalOpen(true)} style={{ padding: '10px 14px', borderRadius: 8, border: '1px solid #D0D5DD', background: '#fff' }}>Assign</button>
              {claim && (claim.estimatedValue || 0) > 500000 && (
                <div data-testid="approval-section" style={{ display: 'inline-flex', gap: 8 }}>
                  {claim.status !== 'pending_approval' && (
                    <button onClick={async () => {
                      if (!requesterId) return
                      await requestApproval({ claimId: claim._id, requestedBy: requesterId, reason: 'High value claim' })
                    }} style={{ padding: '10px 14px', borderRadius: 8 }}>Request Approval</button>
                  )}
                  <button data-testid="approve-claim-button" onClick={() => setApprovalOpen(true)} style={{ padding: '10px 14px', borderRadius: 8 }}>Approve</button>
                </div>
              )}
            </div>
            {assignmentSuccess && <div data-testid="assignment-success" className="success">{assignmentSuccess}</div>}
            <div data-testid="status-history" className="status-history">Current status: {claim.status}</div>

            {/* Comments only */}
            <div style={{ marginTop: 24, borderTop: '1px solid #F2F4F7', paddingTop: 16 }}>
              <h3 style={{ marginTop: 0 }}>Comments</h3>
              <textarea id="claim-comment" placeholder="Add a comment" style={{ width: '100%', minHeight: 80, padding: 10, border: '1px solid #D0D5DD', borderRadius: 8 }} />
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12, gap: 10 }}>
                <button onClick={async () => {
                  const text = (document.getElementById('claim-comment') as HTMLTextAreaElement)?.value
                  if (!text || !text.trim()) return
                  await updateDetails({ claimId: claim._id, updatedBy: requesterId, commentText: text.trim() })
                  ;(document.getElementById('claim-comment') as HTMLTextAreaElement).value = ''
                }} className="primary">Save Changes</button>
              </div>
              {Array.isArray(claim.comments) && claim.comments.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <h4 style={{ margin: '8px 0' }}>Comment Log</h4>
                  <ul style={{ listStyle: 'none', padding: 0, display: 'grid', gap: 8 }}>
                    {claim.comments.slice().reverse().map((c: any, idx: number) => (
                      <li key={idx} style={{ padding: 12, border: '1px solid #EAECF0', borderRadius: 8 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                          <span style={{ fontWeight: 600 }}>{c.byName || 'User'}</span>
                          <span style={{ color: '#667085', fontSize: 12 }}>{new Date(c.at).toLocaleString()}</span>
                        </div>
                        <div style={{ whiteSpace: 'pre-wrap' }}>{c.text}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            </>
            )}

            {activeTab === 'documents' && (
              <div>
                <div data-testid="document-upload-section" style={{ marginBottom: 16, padding: 12, border: '1px dashed #D0D5DD', borderRadius: 8, background: '#F9FAFB' }}>
                  <div style={{ display: 'grid', gap: 8 }}>
                    <input id="file-input" data-testid="file-input" type="file" />
                    <select id="document-type" data-testid="document-type" defaultValue="other">
                      <option value="death_certificate">Death Certificate</option>
                      <option value="medical_record">Medical Record</option>
                      <option value="physician_statement">Physician Statement</option>
                      <option value="beneficiary_form">Beneficiary Form</option>
                      <option value="disability_assessment">Disability Assessment</option>
                      <option value="correspondence">Correspondence</option>
                      <option value="other">Other</option>
                    </select>
                    <textarea id="document-description" data-testid="document-description" placeholder="Description" />
                    <div style={{ display: 'flex', gap: 12 }}>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <input id="confidential-checkbox" data-testid="confidential-checkbox" type="checkbox" /> Confidential
                      </label>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <input id="hipaa-compliant-checkbox" data-testid="hipaa-compliant-checkbox" type="checkbox" /> HIPAA compliant
                      </label>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <input id="new-version-checkbox" data-testid="new-version-checkbox" type="checkbox" /> New version
                      </label>
                    </div>
                    {/* Parent document dropdown removed */}
                    <button data-testid="upload-button" style={{ padding: '8px 12px', borderRadius: 8, background: '#7F56D9', color: '#fff' }} onClick={async () => {
                      if (!requesterId || !claim?._id) return
                      const fileInput = document.getElementById('file-input') as HTMLInputElement
                      const file = fileInput?.files?.[0]
                      if (!file) {
                        const err = document.querySelector('[data-testid=upload-error]') as HTMLElement
                        if (err) { err.style.display = 'block'; err.textContent = 'Please select a file' }
                        return
                      }
                      const typeSel = document.getElementById('document-type') as HTMLSelectElement
                      const descEl = document.getElementById('document-description') as HTMLTextAreaElement
                      const confEl = document.getElementById('confidential-checkbox') as HTMLInputElement
                      try {
                        const { url } = await getUploadUrl({}) as any
                        const put = await fetch(url, { method: 'POST', body: file })
                        if (!put.ok) throw new Error('Upload to storage failed')
                        const { storageId } = await put.json()
                        await finalizeUpload({
                          claimId: claim._id,
                          uploadedBy: requesterId,
                          storageId,
                          fileName: file.name,
                          fileType: file.type || 'application/octet-stream',
                          fileSize: file.size,
                          documentType: typeSel?.value || 'other',
                          isConfidential: !!confEl?.checked,
                          description: descEl?.value || '',
                        })
                        const ok = document.querySelector('[data-testid=upload-success]') as HTMLElement
                        if (ok) { ok.style.display = 'block'; ok.textContent = 'Uploaded' }
                        const err = document.querySelector('[data-testid=upload-error]') as HTMLElement
                        if (err) { err.style.display = 'none'; err.textContent = '' }
                      } catch (e) {
                        const err = document.querySelector('[data-testid=upload-error]') as HTMLElement
                        if (err) { err.style.display = 'block'; err.textContent = (e as any)?.message || 'Upload failed' }
                      }
                    }}>Upload</button>
                    <div data-testid="upload-progress" style={{ display: 'none' }} />
                    <div data-testid="upload-progress-bar" style={{ display: 'none' }} />
                    <div data-testid="upload-percentage" style={{ display: 'none' }} />
                    <div data-testid="upload-success" style={{ display: 'none' }} />
                    <div data-testid="upload-error" style={{ display: 'none' }} />
                    <button data-testid="retry-upload" style={{ display: 'none' }}>Retry</button>
                  </div>
                </div>

                <div>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                    <input data-testid="document-search" placeholder="Search" value={docSearchTerm} onChange={(e) => setDocSearchTerm(e.target.value)} style={{ flex: 1, padding: 8, border: '1px solid #D0D5DD', borderRadius: 8 }} />
                    <select data-testid="document-type-filter" value={docTypeFilter} onChange={(e) => setDocTypeFilter(e.target.value)} style={{ padding: 8, border: '1px solid #D0D5DD', borderRadius: 8 }}>
                      <option value="">All</option>
                      <option value="medical_record">Medical record</option>
                      <option value="death_certificate">Death certificate</option>
                      <option value="physician_statement">Physician Statement</option>
                      <option value="beneficiary_form">Beneficiary Form</option>
                      <option value="disability_assessment">Disability Assessment</option>
                      <option value="correspondence">Correspondence</option>
                      <option value="other">Other</option>
                    </select>
                    <button data-testid="clear-filters" onClick={() => { setDocSearchTerm(''); setDocTypeFilter('') }} style={{ padding: '8px 12px', borderRadius: 8 }}>Clear</button>
                  </div>
                  <div data-testid="document-list" style={{ display: 'grid', gap: 8 }}>
                    {filteredDocs.length > 0 ? (
                      filteredDocs.map((d: any, idx: number) => (
                        <div key={d._id} className="document-item" style={{ display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', gap: 8, padding: 8, border: '1px solid #EAECF0', borderRadius: 8 }}>
                          <div>
                            <div style={{ fontWeight: 600 }}>{d.fileName}</div>
                            <div style={{ fontSize: 12, color: '#667085' }}>Type: {d.documentType} • {formatFileSize(d.fileSize)}</div>
                            <span data-testid={`document-type-${d.documentType}`} style={{ display: 'none' }} />
                            {d.requiresHIPAACompliance && <span data-testid="hipaa-indicator" style={{ display: 'none' }} />}
                            {d.accessLevel === 'confidential' && <span data-testid="confidential-document" style={{ display: 'none' }} />}
                          </div>
                          <div>
                            <a data-testid={`download-document-${idx+1}`} href="#" onClick={async (e) => { e.preventDefault(); try {
                              if (!requesterId) return
                              const res = await getDocumentDownloadUrl({ documentId: d._id, requesterId, claimId: claim?._id }) as any
                              if (res?.url) window.open(res.url, '_blank')
                            } catch {} }} style={{ textDecoration: 'none' }}>Download</a>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div style={{ color: '#667085' }}>No documents yet.</div>
                    )}
                  </div>
                </div>
              </div>
            )}
            {activeTab === 'documents' && (
              <div style={{ marginTop: 16 }}>
                <h4 style={{ margin: '8px 0' }}>Recent Document Activity</h4>
                <div style={{ display: 'grid', gap: 6 }}>
                  {(recentDocAudits || []).map((a: any, i: number) => (
                    <div key={i} style={{ fontSize: 12, color: a.success ? '#344054' : '#B42318' }}>
                      [{new Date(a.timestamp).toLocaleTimeString()}] {a.action} {a.success ? '' : `- ${a.errorMessage || ''}`}
                    </div>
                  ))}
                  {(!recentDocAudits || recentDocAudits.length === 0) && (
                    <div style={{ fontSize: 12, color: '#667085' }}>No recent document activity.</div>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div>Loading...</div>
        )}
      </div>
      {statusModalOpen && (
        <div data-testid="status-modal" className="modal" style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div className="modal-content" style={{
            width: '100%', maxWidth: 520, background: '#fff', borderRadius: 12, boxShadow: '0 10px 30px rgba(16,24,40,0.18)', overflow: 'hidden'
          }}>
            <div style={{ padding: 20, borderBottom: '1px solid #F2F4F7' }}>
              <h3 style={{ margin: 0, fontSize: 18 }}>Update Status</h3>
              <p style={{ margin: '6px 0 0', color: '#667085', fontSize: 13 }}>Choose the new status for this claim and add an optional note.</p>
            </div>
            <div style={{ padding: 20, display: 'grid', gap: 12 }}>
              <label style={{ fontSize: 13, color: '#344054' }}>New Status</label>
              <select data-testid="new-status" value={newStatus} onChange={(e) => setNewStatus(e.target.value)} style={{ padding: 10, borderRadius: 8, border: '1px solid #D0D5DD' }}>
                <option value="investigating">Investigating</option>
                <option value="pending_docs">Pending Documents</option>
                <option value="approved">Approved</option>
                <option value="denied">Denied</option>
                <option value="closed">Closed</option>
              </select>
              <label style={{ fontSize: 13, color: '#344054' }}>Note</label>
              <textarea data-testid="status-note" placeholder="Add note (optional)" value={statusNote} onChange={(e) => setStatusNote(e.target.value)} style={{ minHeight: 90, padding: 12, borderRadius: 8, border: '1px solid #D0D5DD' }} />
            </div>
            <div className="modal-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: 16, borderTop: '1px solid #F2F4F7' }}>
              <button onClick={() => setStatusModalOpen(false)} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #D0D5DD', background: '#fff' }}>Cancel</button>
              <button data-testid="confirm-status-update" onClick={submitStatus} disabled={!canSubmitStatus} className="primary" style={{ padding: '8px 14px', borderRadius: 8 }}>
                Update Status
              </button>
            </div>
          </div>
        </div>
      )}
      {assignmentModalOpen && (
        <div data-testid="assignment-modal" className="modal">
          <div className="modal-content">
            <h3>Assign Claim</h3>
            <AdjustersDropdown
              value={assigneeId}
              onChange={setAssigneeId}
              placeholder="Select Adjuster"
            />
            <div className="modal-actions">
              <button onClick={() => setAssignmentModalOpen(false)}>Cancel</button>
              <button data-testid="confirm-assignment" onClick={submitAssignment} disabled={!canAssign}>Assign</button>
            </div>
          </div>
        </div>
      )}
      {approvalOpen && (
        <div data-testid="approval-modal" className="modal">
          <div className="modal-content">
            <h3>Approve Claim</h3>
            <div className="form-field">
              <label>Approved Amount</label>
              <input data-testid="approved-amount" value={approvedAmount} onChange={(e) => setApprovedAmount(e.target.value)} />
            </div>
            <div className="form-field">
              <label>Notes</label>
              <textarea data-testid="approval-notes" value={approvalNotes} onChange={(e) => setApprovalNotes(e.target.value)} />
            </div>
            <div className="modal-actions">
              <button onClick={() => setApprovalOpen(false)}>Cancel</button>
              <button data-testid="confirm-approval" onClick={async () => {
                if (!requesterId || !claim?._id) return
                const amt = Number(approvedAmount)
                if (Number.isNaN(amt)) return
                await approveClaim({ claimId: claim._id, approvedBy: requesterId, approvedAmount: amt, notes: approvalNotes })
                setApprovalOpen(false)
              }}>Confirm</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ClaimDetailsPage